import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Spinner, Pagination, PaginationItem, PaginationLink, Label, UncontrolledTooltip,
  Container, CardBody, Button, Modal, ModalHeader, PopoverBody, Popover,
} from "reactstrap";
import {Link, useLocation} from 'react-router-dom';
import Select from 'react-select';
import CardItem from 'components/PurpleTheme/CardItem';
import {
  disassociateAlertFromLambda,
  getAccountLambdas,
  getAlertsBasedOnResource,
  getMappedLambdaAlert,
  getMasterResources, getAvailableRoles
} from "../../store/actions";
import {mapAlertToLambda} from "../../store/daemon/actions"
import {getStateSpecificAWSLogo} from "../../helpers/component_helpers";

const customStyles = {
  control: (provided, state) => ({
    ...provided,
    background: '#382b42',
    borderRadius: '4px',
    color: "white",
    borderColor: state.isFocused ? '#7367f0' : '#404656',
    boxShadow: state.isFocused ? '0 0 0 1px #7367f0' : 'none',
    minHeight: '42px',
    '&:hover': {
      borderColor: '#7367f0'
    }
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#7367f0'
      : state.isFocused
        ? '#2d2941'
        : '#382b42',
    color: '#ffffff',
    '&:active': {
      backgroundColor: '#7367f0'
    }
  }),
  menu: (provided) => ({
    ...provided,
    background: '#382b42',
    marginTop: '0',
    boxShadow: '0 5px 10px rgba(0,0,0,0.2)',
    zIndex: 999
  }),
  menuList: (provided) => ({
    ...provided,
    padding: '0',
    maxHeight: '300px'
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#ffffff'
  }),
  input: (provided) => ({
    ...provided,
    color: '#ffffff'
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#b8c2cc'
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#b8c2cc',
    '&:hover': {
      color: '#ffffff'
    }
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: '#404656'
  })
};

const LambdaAlertMapPage = () => {
  const dispatch = useDispatch();
  const [selectedMonitoringSystem, setSelectedMonitoringSystem] = useState("Amazon Web Services");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const monitoring_system_id = queryParams.get('monitoring_system_id');
  const [selectedMasterResource, setSelectedMasterResource] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [expandedResourceId, setExpandedResourceId] = useState(null);
  const [selectedAlerts, setSelectedAlerts] = useState({});
  const [showMapModal, setShowMapModal] = useState(false);
  const [currentHoverItem, setCurrentHoverItem] = useState(null);
  const [popoverOpen, setPopoverOpen] = useState({});
  const [deployedLambda, setDeployedLambda] = useState(null);
  const [allAlertsSelected, setAllAlertsSelected] = useState({});
  const { master_resources } = useSelector((state) => ({
    master_resources: state.Dashboard.master_resources || [],
  }));

  const { resource_alerts, mapped_alerts , deployed_account_lambdas, available_roles} = useSelector((state) => ({
    resource_alerts: state.Daemon.resource_alerts || {},
    mapped_alerts: state.Daemon.mapped_alerts || [],
    deployed_account_lambdas: state.Daemon.deployed_account_lambdas || [],
    available_roles: state.Daemon.available_roles || []
  }));

  const deployed_lambdas = deployed_account_lambdas

  const filteredMasterResources = master_resources
    .filter(resource => resource.monitoring_system_name === selectedMonitoringSystem)
    .map(resource => ({
      value: resource.id,
      label: (
        <div className="align-items-center" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img
            className="avatar-xs rounded-circle"
            src={getStateSpecificAWSLogo(resource.name)}
            alt=""
          />
          {resource.name}
        </div>
      )
    }));

  useEffect(() => {
    dispatch(getMasterResources());
    if (monitoring_system_id) {
      dispatch(getAccountLambdas(monitoring_system_id))
    }
    dispatch(getAvailableRoles())
  }, [dispatch]);

  const deployedLambdaOptions = deployed_lambdas?.map(deployed_lambdas => ({
      value: deployed_lambdas.id,
      label: deployed_lambdas.name
    }));

  const roleOptions = available_roles?.map(role => ({
    value: role,
    label: role,
  }));

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedMonitoringSystem || !selectedMasterResource ) return;
    const pageFilters = {
      monitoring_system: selectedMonitoringSystem,
      master_resource_id: selectedMasterResource.value,
      page: currentPage,
      limit: itemsPerPage,
      monitoring_system_id: monitoring_system_id
    };
    const mapped_alerts_payload = {
      master_resource_id: selectedMasterResource.value,
      monitoring_system: selectedMonitoringSystem,
      monitoring_system_id: monitoring_system_id
    }
    dispatch(getAlertsBasedOnResource(pageFilters));
    dispatch(getMappedLambdaAlert(mapped_alerts_payload));
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    if (selectedMonitoringSystem && selectedMasterResource) {
      const pageFilters = {
        monitoring_system: selectedMonitoringSystem,
        master_resource_id: selectedMasterResource.value,
        page: pageNumber,
        limit: itemsPerPage,
        monitoring_system_id: monitoring_system_id
      };
      dispatch(getAlertsBasedOnResource(pageFilters));
    }
  };
  const headers = [
    {name:"Resource Name", width:4},
    {name:"Type", width:3},
    {name:"Environment", width:3},
    {name:"Action", width:2}
  ]

  const totalPages = Math.ceil((resource_alerts?.count || 0) / itemsPerPage);
  let startPage = Math.max(currentPage - 1, 1);
  let endPage = Math.min(startPage + 2, totalPages);
  if (endPage === totalPages) {
    startPage = Math.max(totalPages - 2, 1);
  }

  const toggleResourceExpansion = (resourceId) => {
    setExpandedResourceId(expandedResourceId === resourceId ? null : resourceId);
  };

  const setAlertSelection = (alertId, value) => {
    setSelectedAlerts(prev => ({
      ...prev,
      [alertId]: value
    }));
  };

  const togglePopover = (id) => {
    setPopoverOpen(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const getMappedAlertInfo = (alertId) => {
    const mapping = mapped_alerts.find(m => m.alert_id === alertId);
    if (!mapping) return null;
    return {
      lambda_name: mapping.lambda_name,
      lambda_arn: mapping.lambda_arn,
      lambda_id: mapping.lambda_id,
      role_name: mapping.role_name,
    };
  };

  const handleOpenMapModal = () => {
    setShowMapModal(true);
  };

  const handleMapAlerts = () => {
    const alertIds = Object.keys(selectedAlerts).filter(id => selectedAlerts[id]);
    if (!alertIds.length || !deployedLambda) {
      return;
    }
    dispatch(mapAlertToLambda({
      alert_ids: alertIds,
      deployed_lambda_id: deployedLambda.value
    }));
    setShowMapModal(false);
    setSelectedAlerts({});
  };

  const removeAlertFromSelection = (alertId) => {
    setSelectedAlerts(prev => {
      const newSelection = { ...prev };
      delete newSelection[alertId];
      return newSelection;
    });
  };

  const handleDisassociateAlerts = (alertId, lambda_id) => {
    dispatch(disassociateAlertFromLambda({
      alert_id: alertId,
      deployed_lambda_id: lambda_id
    }));
  }

  function toggleAllAlerts(resource) {
    const oppositeValue = !allAlertsSelected[resource.id];
    setAllAlertsSelected(prev => ({...prev, [resource.id]: oppositeValue}));
    resource.alerts.forEach(alert => {
      if (alert) {
        setAlertSelection(alert.id, oppositeValue);
      }
    });
  }
  return (
    <Fragment>
      <Container fluid className="py-4">
        <Row className="mb-4">
          <Col md={12}>
            <h4 className="text-white">Associate Alerts with Lambda Functions</h4>
          </Col>
        </Row>
        <Row className="mb-2 align-items-end">
          <Col md={3}>
            <div className="form-group">
              <Label className="text-white">Resource Type</Label>
              <Select
                styles={customStyles}
                options={filteredMasterResources}
                value={selectedMasterResource}
                onChange={(selectedOption) => {
                  setSelectedMasterResource(selectedOption);
                  // handleSubmit();
                }}
                isDisabled={!selectedMonitoringSystem}
                placeholder="Select Resource Type"
                className="react-select-container"
                classNamePrefix="react-select"
              />
            </div>
          </Col>
          <Col md={3} className="d-flex align-items-end">
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={!selectedMonitoringSystem || !selectedMasterResource}
            >
              Submit
            </button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={12}>
            <Pagination aria-label="pagination navigation"
                        listClassName="justify-content-end pagination-rounded" className="mb-3">
              <PaginationItem
                key={"pagination-item-" + Math.max((currentPage - 1 ||  1), 1)}
                disabled={currentPage === 1}
              >
                <PaginationLink
                  href="#"
                  onClick={() => handlePageChange(Math.max((currentPage - 1 ||  1), 1))}>
                  <i className="mdi mdi-chevron-left"/>
                </PaginationLink>
              </PaginationItem>
              {[...Array(endPage - startPage + 1)].map((_, index) => (
                <PaginationItem key={"pagination-item-" + index + (startPage + index)}
                                active={currentPage === startPage + index}>
                  <PaginationLink href="#" onClick={() => handlePageChange(startPage + index)}>
                    {startPage + index}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem key={"pagination-item" + Math.min(currentPage + 1, totalPages)}
                              disabled={currentPage === totalPages}>
                <PaginationLink
                  href="#"
                  onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}>
                  <i className="mdi mdi-chevron-right"/>
                </PaginationLink>
              </PaginationItem>
            </Pagination>
          </Col>
        </Row>
        <div className="header d-flex">
          <Row className="w-100">
            {headers.map((header, index) => (
              <Col md={header.width} lg={header.width} key={index}>
                <div className="d-flex align-items-center justify-content-center p-2 py-3">
                  {header.name}
                </div>
              </Col>
            ))}
          </Row>
        </div>
        {resource_alerts?.isLoading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : resource_alerts?.resources?.length > 0 ? (
          <>
            <Row className="mb-1 scrollable-container">
              <Col md={12}>
                <div className="table-responsive" style={{ minHeight: '400px' }}>
                  {resource_alerts?.resources?.map((resource, index) => (
                    <CardItem
                      key={`resource-card-${index}`}
                      className="card-item mb-3"
                      cursor="pointer"
                      onMouseEnter={() => setCurrentHoverItem(resource.id)}
                      onMouseLeave={() => setCurrentHoverItem(null)}
                    >
                      <CardBody className="pt-0 pr-2 pb-1"
                                onClick={() => toggleResourceExpansion(resource.id)}
                      >
                        <Row className="align-items-center">
                          <Col md={4} className="padding-t-15 align-items-start">
                            <div style={{ textAlign: 'center' }}>
                              <h6
                                className="resource-name-truncate text-white"
                                id={`resource-name-${resource.id}`}
                                onMouseEnter={() => togglePopover(`resource-name-${resource.id}`)}
                                onMouseLeave={() => togglePopover(`resource-name-${resource.id}`)}
                              >
                                {resource.name}
                              </h6>
                              <Popover
                                isOpen={popoverOpen[`resource-name-${resource.id}`] || false}
                                target={`resource-name-${resource.id}`}
                                placement="top"
                              >
                                <PopoverBody>{resource.name}</PopoverBody>
                              </Popover>
                            </div>
                          </Col>
                          <Col md={3} className="padding-t-15">
                            <div
                              className="mb-1 text-center resource-type-truncate text-white"
                              id={`resource-type-${resource.id}`}
                            >
                              {resource.type}
                            </div>
                          </Col>
                          <Col md={3} className="padding-t-15">
                            <div className="mb-1 text-center text-white">
                              {resource.environment}
                            </div>
                          </Col>
                          <Col md={2} className="padding-t-15">
                            <div className="d-flex gap-3 justify-content-center">
                              <Link
                                to="#"
                                className="text-white"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleResourceExpansion(resource.id);
                                }}
                              >
                                <i
                                  className={`mdi mdi-chevron-${expandedResourceId === resource.id ? 'up' : 'down'}`}
                                  id={`expandtooltip-${index}`}
                                />
                                <UncontrolledTooltip placement="top" target={`expandtooltip-${index}`}>
                                  {expandedResourceId === resource.id ? 'Collapse' : 'Expand'}
                                </UncontrolledTooltip>
                              </Link>
                            </div>
                          </Col>
                        </Row>
                        {expandedResourceId === resource.id && (
                          <div className="mt-3 border-top pt-3">
                            <Row className="mb-3">
                              <Col md="12">
                                <div className="d-flex justify-content-end align-items-center">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={handleOpenMapModal}
                                    disabled={!Object.values(selectedAlerts).some(val => val)}
                                  >
                                    Map Selected
                                  </Button>
                                </div>
                              </Col>
                            </Row>
                            {resource.alerts?.length > 0 ? (
                              <>
                                <div className="header">
                                  <Row className="mb-1 mt-2">
                                    <Col md={1}>
                                      <div className="text-white text-center">
                                        <i
                                          className={`bx ${
                                            allAlertsSelected[resource.id] ?
                                              'bx-check-square' : 'bx-square'
                                          } bx-sm`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleAllAlerts(resource)
                                          }}
                                          style={{ cursor: 'pointer' }}
                                        />
                                      </div>
                                    </Col>
                                    <Col md={4}>
                                      <div className="text-white text-center">Alert Name</div>
                                    </Col>
                                    <Col md={3}>
                                      <div className="text-white text-center">Lambda Name</div>
                                    </Col>
                                    <Col md={3}>
                                      <div className="text-white text-center">Role Name</div>
                                    </Col>
                                    <Col md={1}>
                                      <div className="text-white text-center">Action</div>
                                    </Col>
                                  </Row>
                                </div>
                                {resource.alerts.map(alert => {
                                  const mapping = getMappedAlertInfo(alert.id);
                                  return (
                                    <Row
                                      key={alert.id}
                                      className="mb-2 align-items-center"
                                      onMouseEnter={() => setCurrentHoverItem(alert.id)}
                                      onMouseLeave={() => setCurrentHoverItem(null)}
                                      style={{
                                        backgroundColor: currentHoverItem === alert.id ? '#2d2941' : '',
                                        cursor: 'pointer'
                                      }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAlertSelection(alert.id, !selectedAlerts[alert.id]);
                                      }}
                                    >
                                      <Col md={1}>
                                        <div className="text-center">
                                          <i
                                            className={`bx ${
                                              selectedAlerts[alert.id] ? 'bx-check-square' : 'bx-square'
                                            } bx-sm`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setAlertSelection(alert.id, !selectedAlerts[alert.id]);
                                            }}
                                            style={{ cursor: 'pointer' }}
                                          />
                                        </div>
                                      </Col>
                                      <Col md={4}>
                                        <div
                                          className="text-center text-white resource-name-truncate"
                                          id={`alert-name-${alert.id}`}
                                          onMouseEnter={() => togglePopover(`alert-name-${alert.id}`)}
                                          onMouseLeave={() => togglePopover(`alert-name-${alert.id}`)}
                                        >
                                          {alert.name}
                                        </div>
                                        <Popover
                                          isOpen={popoverOpen[`alert-name-${alert.id}`] || false}
                                          target={`alert-name-${alert.id}`}
                                          placement="top"
                                        >
                                          <PopoverBody>{alert.name}</PopoverBody>
                                        </Popover>
                                      </Col>
                                      <Col md={3}>
                                        <div className="text-center text-white">
                                          {mapping ? mapping.lambda_name : '-'}
                                        </div>
                                      </Col>
                                      <Col md={3}>
                                        <div className="text-center text-white">
                                          {mapping ? mapping.role_name : '-'}
                                        </div>
                                      </Col>
                                      <Col md={1}>
                                        <div className="text-center">
                                          {mapping && (
                                            <>
                                              <i
                                                className="mdi font-size-24 mdi-delete deleteUserIcon text-danger"
                                                id={`delete-tooltip-alert-${alert.id.replace(/[-]/g, "")}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDisassociateAlerts(alert.id, mapping.lambda_id);
                                                }}
                                                style={{ cursor: 'pointer' }}
                                              />
                                              <UncontrolledTooltip
                                                placement="top"
                                                target={`delete-tooltip-alert-${alert.id.replace(/[-]/g, "")}`}
                                              >
                                                Remove Mapping
                                              </UncontrolledTooltip>
                                            </>
                                          )}
                                        </div>
                                      </Col>
                                    </Row>
                                  );
                                })}
                              </>
                            ) : (
                              <div className="text-center py-3 text-white">
                                No alerts found for this resource
                              </div>
                            )}
                          </div>
                        )}
                      </CardBody>
                    </CardItem>
                  ))}
                </div>
              </Col>
            </Row>
            <Row className="mb-4">
              <h6 style={{textAlign: 'right', marginTop: '5px', marginBottom:'20px'}}>Showing {(resource_alerts?.resources?.length || 0)} of {resource_alerts?.count} Results</h6>
            </Row>
          </>
        ) : selectedMonitoringSystem && selectedMasterResource ? (
          <Row>
            <Col md={12}>
              <div className="text-center py-5 text-white">
                No resources found for the selected criteria
              </div>
            </Col>
          </Row>
        ) : (
          <Row>
            <Col md={12}>
              <div className="text-center py-5 text-white">
                Please select a Resource Type to view Resources
              </div>
            </Col>
          </Row>
        )}
        <Modal
          isOpen={showMapModal}
          centered
          size="xl"
          onClosed={() => setShowMapModal(false)}
        >
          <div className="modal-content">
            <ModalHeader toggle={() => setShowMapModal(false)}>
              Map Alerts to Lambda
            </ModalHeader>
            <div className="modal-body">
              <Row>
                <Col lg="7">
                  <h5 className="text-primary">Selected Alerts</h5>
                  <Row>
                    {resource_alerts?.resources?.flatMap(resource =>
                      resource.alerts
                        ?.filter(alert => selectedAlerts[alert.id])
                        .map(alert => {
                          return (
                            <Col md="6" key={alert.id}>
                              <CardBody className="border bg-transparent text-white px-2 my-1">
                                <div className="resource-logo-display mt-1">
                                  <h6
                                    className="pt-2 resource-name-truncate"
                                    id={`modal-alert-name-${alert.id}`}
                                    onMouseEnter={() => togglePopover(`modal-alert-name-${alert.id}`)}
                                    onMouseLeave={() => togglePopover(`modal-alert-name-${alert.id}`)}
                                  >
                                    {alert.name}
                                  </h6>
                                  <Popover
                                    isOpen={popoverOpen[`modal-alert-name-${alert.id}`] || false}
                                    target={`modal-alert-name-${alert.id}`}
                                    placement="top"
                                  >
                                    <PopoverBody>{alert.name}</PopoverBody>
                                  </Popover>
                                </div>
                              </CardBody>
                              <span
                                className="close me-2"
                                onClick={() => removeAlertFromSelection(alert.id)}
                              >×</span>
                            </Col>
                          );
                        })
                    )}
                  </Row>
                </Col>
                <Col lg="5">
                  <h5 className="text-primary">Map To</h5>
                  <Select
                    styles={customStyles}
                    options={deployedLambdaOptions}
                    value={deployedLambda}
                    onChange={setDeployedLambda}
                    placeholder="Select Lambda"
                    className="mt-3"
                  />
                  <Row className="mt-4 justify-content-end">
                    <Col lg="2">
                      <Button
                        color="primary"
                        onClick={handleMapAlerts}
                        disabled={!deployedLambda}
                      >
                        Map
                      </Button>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </div>
          </div>
        </Modal>
      </Container>
    </Fragment>
  );
};

export default LambdaAlertMapPage;